import type { OnboardingConfig } from '@/tenants/tenant-config'

export const ONBOARDING_PREFIX = '/onboarding'

/**
 * Only allow internal, absolute paths as post-onboarding redirect targets.
 * Anything else (external URLs, protocol-relative `//host`, non-strings) is
 * dropped to avoid open-redirects.
 */
export const sanitizeRedirect = (value: unknown): string | undefined =>
	typeof value === 'string' && value.startsWith('/') && !value.startsWith('//') ? value : undefined

export type RootRedirect = { to: '/onboarding/welcome'; redirect?: string } | { to: '/' }

/**
 * Decides whether the root layout should redirect an inbound navigation to the
 * onboarding flow. Pure mirror of the `__root` beforeLoad gate.
 */
export const resolveRootRedirect = (args: {
	pathname: string
	onboarding: OnboardingConfig
	customer: { termsAgreedToAt?: number | null } | null
	termsAcceptedLocally: boolean
	loginRequired: boolean
	isLoggedIn: boolean
}): RootRedirect | null => {
	const { pathname, onboarding, customer, termsAcceptedLocally, loginRequired, isLoggedIn } = args

	const isOnboardingPath = pathname.startsWith(ONBOARDING_PREFIX)

	// Carousel tenants are anonymous ecommerce: no login or terms gate, and no
	// full-screen onboarding flow. Bounce any stray /onboarding navigation (e.g. a
	// stale URL after switching tenants) back home; otherwise allow through.
	if (onboarding.style === 'carousel') {
		return isOnboardingPath ? { to: '/' } : null
	}

	// The onboarding routes render their own flow and must never self-redirect.
	if (isOnboardingPath) {
		return null
	}

	const termsLastUpdatedAtMs = new Date(onboarding.termsLastUpdatedAt).getTime()
	const serverTermsAccepted = (customer?.termsAgreedToAt ?? 0) >= termsLastUpdatedAtMs
	const hasAcceptedTerms = termsAcceptedLocally || serverTermsAccepted
	const isAuthenticated = !loginRequired ? true : isLoggedIn

	if (!isAuthenticated || !hasAcceptedTerms) {
		return {
			to: '/onboarding/welcome',
			redirect:
				pathname !== '/' && !pathname.startsWith(ONBOARDING_PREFIX) ? pathname : undefined
		}
	}

	return null
}

/**
 * Home (`/`) redirect for tenants without ecommerce: loyalty customers land on
 * the loyalty tab, everyone else on receipts. Returns null when ecom is on.
 */
export const resolveHomeRedirect = (features: {
	ecomEnabled: boolean
	loyaltyCardEnabled: boolean
}): '/loyalty' | '/receipts' | null => {
	if (features.ecomEnabled) {
		return null
	}
	return features.loyaltyCardEnabled ? '/loyalty' : '/receipts'
}

export type IntroPage = 'TERMS' | 'LOYALTY' | 'RECEIPTS' | 'WALLET'

/**
 * Builds the ordered list of full-screen intro pages. Terms come first (only
 * when the customer still owes acceptance), followed by the loyalty pages, with
 * the wallet page omitted when the customer has no loyalty card.
 */
export const buildIntroPages = (args: {
	needsTerms: boolean
	showLoyaltyPages: boolean
	walletNotFound: boolean
}): IntroPage[] => {
	const { needsTerms, showLoyaltyPages, walletNotFound } = args
	const result: IntroPage[] = []
	if (needsTerms) {
		result.push('TERMS')
	}
	if (showLoyaltyPages) {
		result.push('LOYALTY')
		result.push('RECEIPTS')
		if (!walletNotFound) {
			result.push('WALLET')
		}
	}
	return result
}
